import { useQueries } from "@tanstack/react-query";
import { fetchClient } from "../../API/fetchClient";
import type { KpisFilters } from "./useGetKpisAnalytics";
import type { CarboneEmpreinteResponse } from "./useGetCarboneEmpreinte";

const CARBONE_ENDPOINTS = [
  "/core/analytics/energie-carbone/",
  "/core/analytics/elevage-carbone/",
  "/core/analytics/sol-carbone/",
  "/core/analytics/gaz-frigo-carbone/",
  "/core/analytics/transport-carbone/",
  "/core/analytics/achat-carbone/",
  "/core/analytics/immobilisation-carbone/",
  "/core/analytics/dechets-carbone/",
] as const;

const N_ENDPOINTS = CARBONE_ENDPOINTS.length;

export interface CarboneDimensionItem {
  id: number;
  name: string;
  total: number;
}

interface UseGetCarboneByDimensionOptions {
  filters: KpisFilters;
  dimensionKey: "activite_ids" | "filiere_ids";
  items: { id: number; name: string }[];
  enabled?: boolean;
}

export function useGetCarboneByDimension({
  filters,
  dimensionKey,
  items,
  enabled = true,
}: UseGetCarboneByDimensionOptions): { data: CarboneDimensionItem[]; isLoading: boolean } {
  const isEnabled = enabled && items.length > 0 && !!filters.from_period && !!filters.to_period;

  const results = useQueries({
    queries: items.flatMap((item) =>
      CARBONE_ENDPOINTS.map((endpoint, endpointIdx) => ({
        queryKey: ["carboneByDim", dimensionKey, item.id, endpointIdx, filters],
        queryFn: async (): Promise<number> => {
          const body: Record<string, unknown> = {};
          Object.entries(filters).forEach(([k, v]) => {
            if (v !== null && v !== undefined) body[k] = v;
          });
          body[dimensionKey] = [item.id];

          const resp = await fetchClient<CarboneEmpreinteResponse>(endpoint, {
            method: "POST",
            body,
          });
          if (resp.error || !resp.data) return 0;
          return (resp.data.summary ?? []).reduce((s, i) => s + i.total_co2, 0);
        },
        enabled: isEnabled,
        staleTime: 1000 * 60 * 5,
      }))
    ),
  });

  const isLoading = results.some((r) => r.isLoading);

  const data: CarboneDimensionItem[] = items
    .map((item, itemIdx) => {
      const slice = results.slice(itemIdx * N_ENDPOINTS, (itemIdx + 1) * N_ENDPOINTS);
      const total = slice.reduce((s, r) => s + ((r.data as number) ?? 0), 0);
      return { id: item.id, name: item.name, total };
    })
    .filter((d) => d.total > 0)
    .sort((a, b) => b.total - a.total);

  return { data, isLoading };
}
